import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import {
  Breadcrumb as BreadcrumbCore,
  BreadcrumbItem,
} from "components/core/Breadcrumbs/Breadcrumb";
import { Modal } from "components/core";
import { useNavigate, useParams } from "react-router-dom";
import { NavContext } from "context/NavProvider";
import { isNullEmptyOrWhitespace, isNull, toReadableString } from "helpers/stringUtilities";
import useQuery from "hooks/useQuery";
import BreadcrumbItemMenu from "./BreadcrumbItemMenu";
import BreadcrumbItemFarm from "./BreadcrumbItemFarm";
import BreadcrumbItemHouse from "./BreadcrumbItemHouse";
import { buildListPageUrl } from "helpers/redirectUtilities";
import { useActiveMenu } from "hooks/useActiveMenu";
import { useFarmGetMany } from "hooks/useFarm";
import { useFormGetMany } from "hooks/useForm";

/**
 * @param {*} params
 * @returns 
 */
const Breadcrumb = ({
  showHome = true,
  showFarm = true,
  showHouse = true,
  farmRequired = undefined,
  houseRequired = undefined,
  menuRequired = true,
  children = undefined,
  ...other
}) => {
  const query = useQuery();
  const { activeMenu } = useActiveMenu();
  const moduleFeatureGroup = activeMenu?.ModuleFeatureGroup;
  const module = activeMenu?.Module;

  const farmId = query.get("farmId");
  const houseId = query.get("houseId");
  const { view, formId } = useParams();

  const navigate = useNavigate();
  const { data: forms } = useFormGetMany();
  const { data: farms } = useFarmGetMany();
  const { optionsToShow, setOptionsToHide, setOptionsToShow } =
    useContext(NavContext);
  const [house, setHouse] = useState(undefined);

  const navRef = useRef(undefined);
  const abortControllerRef = useRef(undefined);

  const farm = useMemo(() => {
    if (farmId === null) {
      return undefined;
    }

    return farms.find(
      (f) => f.FarmCode.toLowerCase() === farmId.toLowerCase()
    );
  }, [farms, farmId]);

  //#region Callbacks

  const handleOpenOption = (id) => {
    setOptionsToShow(id);
  };

  const handleOnShowOptions = (id, ev) => {
    setOptionsToShow(id);

    if (ev) {
      // Keep open menu in viewport
      ev.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  };

  const handleOnHideOptions = (id) => {
    setOptionsToHide(id);
  };

  const handleClickBreadcrumbRoot = () => {
    navigate({
      pathname: "/",
      search: null,
    });
  };

  const form = useMemo(() => {
    let filteredForms = forms.find(
      (f) =>
        f.FormName?.toLowerCase() === formId?.toLowerCase() &&
        f.FormType?.toLowerCase() === moduleFeatureGroup?.toLowerCase() &&
        f.ModuleName?.toLowerCase() === module?.toLowerCase()
    );

    // console.log(module?.toLowerCase(), moduleFeatureGroup?.toLowerCase(), formId?.toLowerCase())

    return filteredForms ?? null;
  }, [forms, module, moduleFeatureGroup, formId]);

  const handleClickBreadcrumbHouse = useCallback(
    (houseId) => {
      const searchParams = new URLSearchParams(
        window.location.search.substring(1)
      );
      searchParams.set("houseId", houseId);
      navigate({
        search: "?" + searchParams.toString(),
      });
    },
    [navigate]
  );

  const handleClickForm = () => {
    // Redirect
    return navigate(buildListPageUrl(activeMenu));
  };

  const hasUnselectedRequirements = () => {
    return (
      (farmRequired && isNullEmptyOrWhitespace(farmId)) ||
      (houseRequired && isNullEmptyOrWhitespace(houseId))
    );
  };

  const removeURLQuery = useCallback(
    (name) => {
      const searchParams = new URLSearchParams(
        window.location.search.substring(1)
      );
      searchParams.delete(name);
      navigate({
        search: "?" + searchParams.toString(),
      });
    },
    [navigate]
  );

  //#endregion

  //#region Side-effects

  /**
   * Mount/Unmount
   */
  useEffect(
    () => {
      abortControllerRef.current = new AbortController();

      return () => {
        abortControllerRef.current.abort();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Set house
   */
  useEffect(() => {
    let house = null;

    if (!isNull(farm) && !isNullEmptyOrWhitespace(houseId)) {
      house = farm.Houses.find(
        (h) => h.HouseNumber.toString() === houseId.toString()
      );
      if (isNull(house)) {
        // Remove houseId from URL
        removeURLQuery("houseId");
      }
    }
    setHouse((prevState) => (prevState !== house ? house : prevState));
  }, [farm, houseId, navigate, handleClickBreadcrumbHouse, removeURLQuery]);

  /**
   * Set options to show
   */
  useEffect(() => {
    const optionsToShow = (() => {
      if (farmRequired && !farmId) {
        // Farm
        return "farm";
      } else if (farm && houseRequired && isNullEmptyOrWhitespace(houseId)) {
        // House
        return "house";
      }
      return false;
    })();
    setOptionsToShow(optionsToShow);
  }, [
    farmId,
    farmRequired,
    houseId,
    houseRequired,
    farm,
    setOptionsToShow,
  ]);

  //#endregion

  //#region event handlers

  // const handleClickBreadcrumbForm = (formId) => {
  //   const searchParams = new URLSearchParams(location.search);
  //   searchParams.set("formId", formId);
  //   history.push({
  //     search: "?" + searchParams.toString(),
  //   });
  // };

  //#endregion

  const portalContainer = document.querySelector("#root");

  return (
    <BreadcrumbCore
      {...other}
      parentRef={navRef}
      onClickHome={handleClickBreadcrumbRoot}
      showHome={showHome}
    >
      {menuRequired && (
        <BreadcrumbItemMenu
          key="menu"
          farm={farm}
          showOptions={optionsToShow === "menu"}
          onShowOptions={(ev) => handleOnShowOptions("menu", ev)}
          onHideOptions={() => handleOnHideOptions("menu")}
        />
      )}
      {farmRequired && (
        <BreadcrumbItemFarm
          key="farm"
          farmId={farmId}
          farm={farm}
          farms={farms}
          showOptions={optionsToShow === "farm"}
          onShowOptions={(ev) => handleOnShowOptions("farm", ev)}
          onHideOptions={() => handleOnHideOptions("farm")}
        />
      )}
      {houseRequired && !isNullEmptyOrWhitespace(farm) && (
        <BreadcrumbItemHouse
          key="house"
          houseId={houseId}
          house={house}
          houses={farm.Houses}
          showOptions={optionsToShow === "house"}
          onShowOptions={(ev) => handleOnShowOptions("house", ev)}
          onHideOptions={() => handleOnHideOptions("house")}
        />
      )}
      {form && (
        <BreadcrumbItem
          key="form"
          title={form.FormTitle}
          subtitle={toReadableString(view)}
          showDivider={false}
          loaded={form !== undefined}
          onClick={handleClickForm}
        />
      )}
      {children}
      {hasUnselectedRequirements() &&
        portalContainer &&
        ReactDOM.createPortal(
          <Modal>
            {farmRequired && !farmId && (
              <p>
                Select a{" "}
                <span
                  className="text-primary cursor-pointer font-medium"
                  onClick={() => handleOpenOption("farm")}
                >
                  farm
                </span>{" "}
                to continue.
              </p>
            )}
            {houseRequired && farmId && !houseId && (
              <p>
                Select a{" "}
                <span
                  className="text-primary cursor-pointer font-medium"
                  onClick={() => handleOpenOption("house")}
                >
                  house
                </span>{" "}
                to continue.
              </p>
            )}
          </Modal>,
          portalContainer
        )}
    </BreadcrumbCore>
  );
};

export default Breadcrumb;
